/**
 * userNotificationStore — learner in-app notifications slice (Zustand).
 *
 *   GET   /api/learners/notifications
 *   GET   /api/learners/notifications/:id
 *   GET   /api/learners/notifications/unread-count
 *   PATCH /api/learners/notifications/:id/read
 *   PATCH /api/learners/notifications/read-all
 */
import { apiRequest, normalizeList } from "./userUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const userNotificationInitialState = {
  notifications: [],
  notificationsLoading: false,
  notificationsError: null,
  notificationsTotal: 0,
  notificationsPages: 1,

  notificationDetail: null,
  notificationDetailLoading: false,
  notificationDetailError: null,

  unreadCount: 0,
  unreadCountLoading: false,
  unreadCountError: null,

  notificationMutationLoading: false,
  notificationMutationError: null,
};

export function createUserNotificationActions(set, get) {
  return {
    // GET /api/learners/notifications
    fetchNotifications: async (params = {}) => {
      set({ notificationsLoading: true, notificationsError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/learners/notifications${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeList(data, ["notifications"]);
        set({
          notificationsLoading: false,
          notifications: items,
          notificationsTotal: total,
          notificationsPages: totalPages,
        });
        return { success: true, items, total, totalPages };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load notifications");
        set({ notificationsLoading: false, notificationsError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/notifications/:id
    fetchNotification: async (id) => {
      set({ notificationDetailLoading: true, notificationDetailError: null });
      try {
        const data = await apiRequest(get, `/api/learners/notifications/${id}`);
        const notification = data?.data ?? data?.notification ?? data;
        set({ notificationDetailLoading: false, notificationDetail: notification });
        return { success: true, data: notification };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load notification");
        set({ notificationDetailLoading: false, notificationDetailError: msg });
        return { success: false, error: msg };
      }
    },

    // GET /api/learners/notifications/unread-count
    fetchUnreadCount: async () => {
      set({ unreadCountLoading: true, unreadCountError: null });
      try {
        const data = await apiRequest(get, "/api/learners/notifications/unread-count");
        const count = Number(data?.count ?? data?.unreadCount ?? data?.data?.count ?? 0) || 0;
        set({ unreadCountLoading: false, unreadCount: count });
        return { success: true, count };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to load unread count");
        set({ unreadCountLoading: false, unreadCountError: msg });
        return { success: false, error: msg };
      }
    },

    // PATCH /api/learners/notifications/:id/read
    markNotificationRead: async (id) => {
      set({ notificationMutationLoading: true, notificationMutationError: null });
      try {
        await apiRequest(get, `/api/learners/notifications/${id}/read`, {
          method: "PATCH",
          body: {},
        });
        set((state) => ({
          notificationMutationLoading: false,
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true, status: "read" } : n
          ),
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to mark notification read");
        set({ notificationMutationLoading: false, notificationMutationError: msg });
        return { success: false, error: msg };
      }
    },

    // PATCH /api/learners/notifications/read-all
    markAllNotificationsRead: async () => {
      set({ notificationMutationLoading: true, notificationMutationError: null });
      try {
        await apiRequest(get, "/api/learners/notifications/read-all", {
          method: "PATCH",
          body: {},
        });
        set((state) => ({
          notificationMutationLoading: false,
          notifications: state.notifications.map((n) => ({ ...n, read: true, status: "read" })),
          unreadCount: 0,
        }));
        return { success: true };
      } catch (err) {
        const msg = toDisplayString(err.message, "Failed to mark all read");
        set({ notificationMutationLoading: false, notificationMutationError: msg });
        return { success: false, error: msg };
      }
    },
  };
}

const userNotificationActions = createUserNotificationActions;
export default userNotificationActions;
